import { PkButton, PkCarousel, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useRef } from 'react';
import { useNavigate } from 'react-router';

import { trackVirtPath } from '../../utils/tracking';
import { isValidElement, isVisibleInViewport } from '../../utils/utility';

import HeroTitle from './HeroTitle';

import { useWindowSize } from '@hooks/useWindowSize';
import Image from '@shared/Image';
import type { Hero } from '@shared-types/uefatv.types';
import { LinkType } from '@shared-types/uefatv.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { competitionFolder, competitionTracking, imgBaseUrl } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const trackingData = (h: Hero, index?: number) => ({
  item_id: `${h.heroId}`,
  item_name: `Hero - ${h.title}`,
  item_category: 'hero',
  item_brand: competitionTracking,
  item_list_name: 'hub-uefatv',
  index,
});

const Hero = ({ heroes }: { heroes: Hero[] }) => {
  const navigate = useNavigate();
  const fallbackImg = `${imgBaseUrl}/imgml/uefacom/${competitionFolder}/fallback.jpg`;
  const tracked = useRef([]);
  const { isMobile } = useWindowSize();

  const handleClick = (h: Hero, action: string, n: number) => (e: CustomEvent) => {
    GoogleTagManagerHelper.gtmCardElementClick(e.target as HTMLElement, {
      ...trackingData(h),
      index: n + 1,
      variant: `${action} action`,
    });
    const link = action === 'primary' ? h.primaryButton.link : h.secondaryButton.link;
    if (link.type === LinkType.CONTENTLINK && link.event?.id) {
      e.preventDefault();
      trackVirtPath(link.event?.id, link.event?.type?.toString());
      navigate(`/${link.event?.type?.toLowerCase()}/${link.event?.id}`);
    }
  };

  if (!heroes?.length) return null;

  const slideChange = (c: any) => {
    if (!c) return;
    const active = c.detail?.activeIndex ?? 0;
    if (isVisibleInViewport(c.target) && !tracked.current.includes(active)) {
      GoogleTagManagerHelper.gtmCardImpression([trackingData(heroes[active], active + 1)]);
      tracked.current.push(active);
    }
  };

  return (
    <div
      pk-theme='dark'
      className={clsx(
        'pk-col',
        'pk-col--span-00-4',
        'pk-col--span-ss-4',
        'pk-col--span-xs-4',
        'pk-col--span-sm-8',
        'pk-col--span-md-12',
        'pk-col--span-lg-12',
        'pk-bg--background',
        styles.hero,
      )}
    >
      <div className='pk-col--content'>
        <div className={styles.heroWrapper}>
          <PkCarousel
            onPkSlideChange={slideChange}
            onLoad={slideChange}
            loop
            effect='fade'
            speed={1000}
            parallax
            breakpoints={{
              0: {
                slidesPerView: 1,
                slidesPerGroup: 1,
                spaceBetween: 0,
              },
            }}
            autoPlay={{ delay: 5000, disableOnInteraction: false }}
            navigationPosition='hidden'
            {...(heroes?.length > 1 ? { pagination: { clickable: true } } : {})}
          >
            {heroes?.map((hero, i) => (
              <div key={hero.heroId}>
                <div role='article' className={styles.heroItem}>
                  <figure className={styles.heroImage} data-swiper-parallax='2%'>
                    <div className='pflazyload-wrapper'>
                      <picture>
                        <Image
                          title={hero.title}
                          className={clsx({ [styles.wideImage]: !isMobile || !hero.background?.portraitImageUrl })}
                          src={
                            isMobile && !!hero.background?.portraitImageUrl
                              ? hero.background.portraitImageUrl
                              : hero.background.imageUrl
                          }
                          fallback={fallbackImg}
                        />
                      </picture>
                    </div>
                  </figure>
                  {isMobile && <HeroTitle hero={hero} />}
                  <div className={styles.heroBody}>
                    <div data-swiper-parallax='-200' className={styles.heroSummary}>
                      {!isMobile && <HeroTitle hero={hero} />}
                      <div className={styles.heroDescription}>{hero.description}</div>
                    </div>

                    {(hero.primaryButton || hero.secondaryButton) && (
                      <div className={styles.heroButtons} data-swiper-parallax='-30'>
                        {hero.primaryButton && isValidElement(hero.primaryButton.link) && (
                          <PkButton linkHref={hero.primaryButton.link?.url} onPkClick={handleClick(hero, 'primary', i)}>
                            <PkIcon name='av-play' />
                            <div className='pk-ml--xs2'>{hero.primaryButton.ctaText}</div>
                          </PkButton>
                        )}
                        {hero.secondaryButton && isValidElement(hero.secondaryButton.link) && (
                          <PkButton
                            linkHref={hero.secondaryButton.link?.url}
                            className={styles.heroSecondaryButton}
                            onPkClick={handleClick(hero, 'secondary', i)}
                          >
                            {hero.secondaryButton.ctaText}
                          </PkButton>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </PkCarousel>
        </div>
      </div>
    </div>
  );
};

export default memo(Hero);
